<template>
  <div class="carousel">
    <div class="carousel-outer-wrapper">
      <div class="carousel-wrapper" :class="[activeClasses, gradientClass]">
        <ol ref="carousel" class="carousel-track">
          <slot></slot>
        </ol>
      </div>

      <div v-if="maxOffset < 0 && !isSingleSlide" class="carousel-nav">
        <button
          class="carousel-nav-arrow left-arrow"
          aria-label="Vorige"
          :disabled="
            !(
              activeClasses.includes('is--active-prev') || activeClasses.includes('is--active-both')
            )
          "
          :class="[
            variantClass,
            {
              'is--active':
                activeClasses.includes('is--active-prev') ||
                activeClasses.includes('is--active-both'),
            },
          ]"
          @click="handleNavigationClick('prev')"
        >
          <slot name="navigation-left">
            <Icon name="eenvandaag-general:arrow-left" role="presentation" />
          </slot>
        </button>
        <button
          class="carousel-nav-arrow right-arrow"
          aria-label="Volgende"
          :disabled="
            !(
              activeClasses.includes('is--active-next') || activeClasses.includes('is--active-both')
            )
          "
          :class="[
            variantClass,
            {
              'is--active':
                activeClasses.includes('is--active-next') ||
                activeClasses.includes('is--active-both'),
            },
          ]"
          @click="handleNavigationClick('next')"
        >
          <slot name="navigation-right">
            <Icon name="eenvandaag-general:arrow-right" role="presentation" />
          </slot>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSimpleCarousel } from '~/composables/useSimpleCarousel';
const emit = defineEmits(['active-slide-change', 'current-index-change']);

const {
  activeSlide,
  maxOffset,
  carousel,
  isSingleSlide,
  activeClasses,
  goToItem,
  handleNavigationClick,
} = useSimpleCarousel();

interface IProps {
  gradient?: 'masked' | 'background';
  variant?: 'dark';
}

const props = withDefaults(defineProps<IProps>(), {
  gradient: 'masked',
  variant: undefined,
});

watch(activeSlide, (newValue) => {
  emit('active-slide-change', newValue);
});

const gradientClass = computed(() => {
  return `is--${props.gradient}-gradient`;
});

const variantClass = computed(() => {
  const classes = [];

  if (props.variant) {
    classes.push(`is--${props.variant}`);
  }

  return classes;
});

defineExpose({
  goToItem, // Expose it for external use
});
</script>

<style scoped lang="postcss">
.carousel {
  display: block;
  width: 100%;
}

.carousel-outer-wrapper {
  --carousel-width: 100vw;
  --carousel-margin: var(--unit-18);

  position: relative;
  display: block;
  width: var(--carousel-width);

  margin-left: calc(-1 * var(--carousel-margin));
  margin-right: calc(-1 * var(--carousel-margin));

  @media (--md-viewport) {
    --carousel-width: 100%;
    --carousel-margin: unset;
  }
}

.carousel-wrapper {
  --carousel-wrapper-padding: 0 var(--unit-18) 0 var(--unit-18);

  display: block;
  overflow: hidden;
  width: 100%;
  padding: var(--carousel-wrapper-padding);
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  @media (--md-viewport) {
    --carousel-wrapper-padding: unset;

    &.is--masked-gradient {
      mask-position:
        0 0,
        100% 0;
      mask-repeat: no-repeat, no-repeat;

      &.is--active-both {
        mask-image: linear-gradient(
          90deg,
          transparent 0%,
          white 10%,
          white calc(100% - 10%),
          transparent 100%
        );
      }

      &.is--active-next {
        mask-image: linear-gradient(90deg, white 0%, white calc(100% - 10%), transparent 100%);
      }

      &.is--active-prev {
        mask-image: linear-gradient(90deg, transparent 0%, white 10%, white 100%);
      }
    }

    &.is--background-gradient {
      &:after {
        pointer-events: none;
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        transition: background 250ms ease;
      }

      &.is--active-both {
        &:after {
          background: linear-gradient(
            90deg,
            white 0%,
            transparent 10%,
            transparent calc(100% - 10%),
            white 100%
          );
        }
      }

      &.is--active-next {
        &:after {
          background: linear-gradient(
            90deg,
            transparent 0%,
            transparent calc(100% - 10%),
            white 100%
          );
        }
      }

      &.is--active-prev {
        &:after {
          background: linear-gradient(90deg, white 0%, transparent 10%, transparent 100%);
        }
      }
    }
  }
}

.carousel-track {
  --carousel-track-gap: var(--unit-12);
  display: flex;
  width: auto;
  gap: var(--carousel-track-gap);

  @media (--md-viewport) {
    --carousel-track-gap: var(--unit-16);
  }

  @media (--lg-viewport) {
    --carousel-track-gap: var(--unit-24);
  }
}

.carousel-track > * {
  display: flex;
  flex-shrink: 0;
}

.carousel-nav {
  display: contents;
  pointer-events: none;
}

.carousel-nav-arrow {
  --carousel-nav-arrow-size: var(--unit-64);
  --carousel-nav-arrow-font-size: var(--unit-32);
  --carousel-nav-arrow-padding: var(--unit-20);

  --background-color: var(--color-neutral-white);
  --arrow-color: var(--color-primary-800);

  &:hover {
    --arrow-color: var(--color-primary-600);
  }

  &.is--dark {
    --background-color: var(--color-primary-700);
    --arrow-color: var(--color-neutral-white);

    &:hover {
      --arrow-color: var(--color-primary-200);
    }
  }

  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  translate: 0 -50%;
  z-index: 10;
  border: none;
  height: var(--carousel-nav-arrow-size);
  width: var(--carousel-nav-arrow-size);
  font-size: var(--carousel-nav-arrow-font-size);
  color: hsl(var(--arrow-color));
  opacity: 0;
  transition:
    opacity 200ms ease-in-out,
    color 200ms ease,
    transform 200ms ease;
  pointer-events: none;
  border-radius: 36px;
  background: hsl(var(--background-color));
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transform: scale(0.85);

  &.left-arrow {
    left: 0;
    translate: -50% -50%;
  }
  &.right-arrow {
    right: 0;
    translate: 50% -50%;
  }

  &.is--active {
    opacity: 1;
    transform: scale(1);
    pointer-events: auto;
  }

  @media (--sm-viewport) {
    display: none;
  }
}
</style>
